import React, { useContext } from 'react';
import * as input from '../connectedForm/inputComponents';
import { default as Form } from '../../components/connectedForm';
import { useSymbolFromCurrency } from '../../utils/CurrencyUtil';
import {
  CarriageCalculationMethod,
  CarriageCostCalculationMethod,
  CostViewPermission,
  ICarriageCostMatrix,
  ISupplierCarriageCosts,
  Maybe,
} from '../../graphql/schema/p4types';
import { BlurTextFieldType, IBlurTextFieldProps } from '../connectedForm/BlurTextField/BlurTextField';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import AppContext from '../Context/AppContext';
import { cloneDeep } from '@apollo/client/utilities';

export enum OrderTotalsOriginModule {
  Purchasing = 'PURCHASING',
}

const useCalculateCarriageCostHint = (
  carriageCalculationMethod?: Maybe<CarriageCostCalculationMethod | CarriageCalculationMethod>,
  carriageCosts?: Maybe<ISupplierCarriageCosts>
) => {
  const currencySymbol = useSymbolFromCurrency('currency');

  if (!carriageCosts) return;

  if (carriageCalculationMethod === undefined || carriageCalculationMethod === 'MANUAL') return;

  //Sort into key order descending
  const unsortedMatrix = cloneDeep(carriageCosts.costMatrix);
  const costMatrix = unsortedMatrix?.sort((first, second) => (first?.key > second?.key ? -1 : 1));

  // Now use the first entry with a value of 0 as our Carriage Free value
  const zeroCarriageEntry: Maybe<ICarriageCostMatrix> = costMatrix?.find((cost) => cost?.value === 0);

  // If we have an entry with a zero cost entry, then build our hint string
  if (zeroCarriageEntry) {
    let unitSymbol: string | undefined = '';
    if (carriageCosts.chargeBasis === 'VALUE') {
      unitSymbol = currencySymbol;
    }

    return `Free Carriage after ${unitSymbol ? unitSymbol : ''}${zeroCarriageEntry.key} by ${
      carriageCosts.chargeBasis
    }`;
  }
};

type IOrderTotalsProps = {
  archived: Maybe<boolean>;
  field?: string;
  carriageCalculationMethod?: Maybe<CarriageCostCalculationMethod | CarriageCalculationMethod>;
  carriageCosts?: Maybe<ISupplierCarriageCosts>;
  currencyCode?: Maybe<string>;
  includePayment?: boolean;
  totalLabel?: string;
  creditRaised?: Maybe<boolean>;
  originModule?: OrderTotalsOriginModule;
  includeInvoicedCarriageInTotals?: boolean;
  carriageReadOnly?: boolean;
};

const OrderTotals = ({
  archived,
  field,
  carriageCalculationMethod,
  carriageCosts,
  currencyCode,
  includePayment,
  totalLabel,
  creditRaised,
  originModule,
  includeInvoicedCarriageInTotals,
  carriageReadOnly,
}: IOrderTotalsProps) => {
  const { values } = useContext(ConnectedFormContext);
  const appContext = useContext(AppContext);
  const labelAddon = currencyCode ? ` (${currencyCode})` : undefined;
  const carriageCostHint = useCalculateCarriageCostHint(carriageCalculationMethod, carriageCosts);

  const idPrefix = field ? `${field}.` : '';

  const paymentTotalField = includePayment ? (
    <Form.Field<IBlurTextFieldProps>
      dataid={`${idPrefix}paymentTotal`}
      datatype="numeric"
      component={input.BlurTextField}
      label={'Payment'}
      labelAddon={labelAddon}
      labelCols={6 / 12}
      inputCols={6 / 12}
      decimals={2}
      readOnly={true}
      type={BlurTextFieldType.Currency}
    />
  ) : null;

  // Only enforce cost view permissions for the purchasing module
  const showCostFields: boolean =
    !originModule ||
    originModule !== OrderTotalsOriginModule.Purchasing ||
    appContext?.fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.All;

  let carriageHint: undefined | string | null;
  if (originModule === OrderTotalsOriginModule.Purchasing) {
    carriageHint = carriageCostHint;
  } else {
    carriageHint =
      values?.carriageInvoiced > 0
        ? `Carriage already invoiced: ${Number(values?.carriageInvoiced).toFixed(2)} Tax: ${Number(
            values?.carriageInvoicedVat
          ).toFixed(2)} `
        : null;
  }

  return (
    <>
      {showCostFields ? (
        <Form.Field<IBlurTextFieldProps>
          datacomponent={'OrderTotals'}
          dataid={`${idPrefix}nettGoodsTotal`}
          datatype="numeric"
          component={input.BlurTextField}
          label={'Goods'}
          labelAddon={labelAddon}
          labelCols={6 / 12}
          inputCols={6 / 12}
          decimals={2}
          readOnly={true}
          type={BlurTextFieldType.Currency}
        />
      ) : null}
      <Form.Field<IBlurTextFieldProps>
        datacomponent={'OrderTotals'}
        dataid={`${idPrefix}carriageTotal`}
        datatype="numeric"
        className="primaryValue"
        component={input.BlurTextField}
        hint={carriageHint}
        label="Carriage"
        labelAddon={labelAddon}
        labelCols={6 / 12}
        inputCols={6 / 12}
        decimals={2}
        readOnly={
          archived || !carriageCalculationMethod || carriageCalculationMethod === 'CALCULATED' || carriageReadOnly
        }
        temporary={true}
        type={BlurTextFieldType.Currency}
      />
      {showCostFields ? (
        <>
          <Form.Field<IBlurTextFieldProps>
            datacomponent={'OrderTotals'}
            dataid={`${idPrefix}vatTotal`}
            datatype="numeric"
            component={input.BlurTextField}
            label={currencyCode ? 'Tax' : 'Vat'}
            labelAddon={labelAddon}
            labelCols={6 / 12}
            inputCols={6 / 12}
            decimals={2}
            readOnly={true}
            type={BlurTextFieldType.Currency}
            reformatLabel={false}
          />
          <Form.Field<IBlurTextFieldProps>
            datacomponent={'OrderTotals'}
            dataid={`${idPrefix}headerTotal`}
            datatype="numeric"
            className="primaryValue"
            component={input.BlurTextField}
            label={totalLabel || 'Order Total'}
            labelAddon={labelAddon}
            labelCols={6 / 12}
            inputCols={6 / 12}
            decimals={2}
            readOnly={true}
            type={BlurTextFieldType.Currency}
            hint={includeInvoicedCarriageInTotals ? 'Including Invoiced Carriage and VAT' : undefined}
          />
        </>
      ) : null}
      {paymentTotalField}
      {creditRaised ? (
        <Form.Field<IBlurTextFieldProps>
          datacomponent={'OrderTotals'}
          dataid={`${idPrefix}creditTotal`}
          component={input.BlurTextField}
          label="Credit"
          labelAddon={labelAddon}
          primary={true}
          decimals={2}
          type={BlurTextFieldType.Currency}
        />
      ) : null}
      {values?.amountOutstanding > 0 ? (
        <Form.Field<IBlurTextFieldProps>
          datacomponent={'OrderTotals'}
          dataid={`${idPrefix}amountOutstanding`}
          component={input.BlurTextField}
          label="Outstanding"
          labelAddon={labelAddon}
          primary={true}
          decimals={2}
          type={BlurTextFieldType.Currency}
          readOnly={true}
        />
      ) : null}
    </>
  );
};

export default OrderTotals;
